import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  Bot,
  GitFork,
  Hand,
  CircleDot,
  Square,
  Database,
  Mail,
  Bell,
  Clock,
  Shield,
  ArrowRightLeft,
  Globe,
  FileCheck,
  Zap,
  Stethoscope,
  CreditCard,
  Calendar,
  FileText,
  Phone,
  Printer,
  AlertTriangle,
} from "lucide-react";
import { cn } from "../../lib/utils";

/* ─── Execution state helpers (shared pattern with Agent Studio) ─── */

function execClasses(data: Record<string, unknown>) {
  if (data.__execState === "active")
    return "ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse shadow-lg shadow-primary/20";
  if (data.__execState === "completed")
    return "opacity-75 ring-1 ring-emerald-500/40";
  if (data.__execState === "error")
    return "ring-2 ring-destructive ring-offset-2 ring-offset-background";
  return "";
}

function ExecBadge({ state }: { state: string | undefined }) {
  if (!state || state === "idle") return null;
  if (state === "active")
    return <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary animate-ping" />;
  if (state === "completed")
    return (
      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[8px]">
        ✓
      </span>
    );
  if (state === "error")
    return (
      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white text-[8px]">
        ✕
      </span>
    );
  return null;
}

/* ─── Base wrapper for consistent node styling ─── */

function WorkflowNodeWrapper({
  children,
  selected,
  data,
  borderColor = "border-border",
  selectedBorderColor = "border-primary",
  dashed,
}: {
  children: React.ReactNode;
  selected: boolean;
  data: Record<string, unknown>;
  borderColor?: string;
  selectedBorderColor?: string;
  dashed?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 bg-card shadow-sm min-w-[170px] transition-all",
        dashed && "border-dashed",
        selected ? `${selectedBorderColor} shadow-md` : borderColor,
        execClasses(data),
      )}
    >
      <ExecBadge state={data.__execState as string | undefined} />
      {children}
    </div>
  );
}

/* ─── Agent Reference Node (picks agent from Agent Studio) ─── */

export const AgentRefNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Agent";
  const agentName = (data.agentName as string) ?? "";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-violet-500">
      <Handle type="target" position={Position.Top} className="!bg-violet-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{name}</p>
            <p className="text-[10px] text-muted-foreground truncate">
              {agentName || "Select agent..."}
            </p>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-violet-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
AgentRefNode.displayName = "AgentRefNode";

/* ─── Decision Node (deterministic conditional branching) ─── */

export const DecisionNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Decision";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-amber-500">
      <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
          <GitFork className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">Conditional Branch</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
DecisionNode.displayName = "DecisionNode";

/* ─── EHR Action Node (read/write patient data) ─── */

const EHR_ACTION_STYLES: Record<string, { icon: React.ReactNode; label: string }> = {
  read: { icon: <Database className="h-4 w-4" />, label: "EHR Read" },
  write: { icon: <Database className="h-4 w-4" />, label: "EHR Write" },
  query: { icon: <Database className="h-4 w-4" />, label: "EHR Query" },
};

export const EhrActionNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "EHR Action";
  const actionType = (data.ehrAction as string) ?? "read";
  const style = EHR_ACTION_STYLES[actionType] ?? EHR_ACTION_STYLES.read;

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-emerald-500">
      <Handle type="target" position={Position.Top} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
          {style.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">{style.label}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
EhrActionNode.displayName = "EhrActionNode";

/* ─── Notification Node (email, SMS, fax, alert) ─── */

const NOTIFY_STYLES: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  email: { icon: <Mail className="h-4 w-4" />, color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-100 dark:bg-orange-900/30" },
  sms: { icon: <Phone className="h-4 w-4" />, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" },
  fax: { icon: <Printer className="h-4 w-4" />, color: "text-zinc-600 dark:text-zinc-400", bg: "bg-zinc-100 dark:bg-zinc-800" },
  alert: { icon: <Bell className="h-4 w-4" />, color: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
  in_app: { icon: <Bell className="h-4 w-4" />, color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-900/30" },
};

export const NotificationNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Notification";
  const channel = (data.channel as string) ?? "email";
  const style = NOTIFY_STYLES[channel] ?? NOTIFY_STYLES.email;

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-orange-500">
      <Handle type="target" position={Position.Top} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.bg, style.color)}>
          {style.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground capitalize">{channel}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-orange-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
NotificationNode.displayName = "NotificationNode";

/* ─── Human Review / HITL Node ─── */

export const HumanReviewNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Human Review";
  const reviewerRole = (data.reviewerRole as string) ?? "";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-rose-500" dashed>
      <Handle type="target" position={Position.Top} className="!bg-rose-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400">
          <Hand className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">
            {reviewerRole || "HITL Approval"}
          </p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-rose-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
HumanReviewNode.displayName = "HumanReviewNode";

/* ─── Timer / Wait Node ─── */

export const TimerNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Wait";
  const delay = (data.delayDescription as string) ?? "";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-purple-500">
      <Handle type="target" position={Position.Top} className="!bg-purple-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
          <Clock className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">{delay || "Timer / Delay"}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-purple-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
TimerNode.displayName = "TimerNode";

/* ─── Data Transform Node ─── */

export const DataTransformNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Transform";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-cyan-500">
      <Handle type="target" position={Position.Top} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400">
          <ArrowRightLeft className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">Data Mapping</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
DataTransformNode.displayName = "DataTransformNode";

/* ─── API Call Node ─── */

export const ApiCallNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "API Call";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-indigo-500">
      <Handle type="target" position={Position.Top} className="!bg-indigo-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
          <Globe className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">External System</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-indigo-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
ApiCallNode.displayName = "ApiCallNode";

/* ─── Compliance Check Node ─── */

export const ComplianceNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Compliance Check";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-red-500">
      <Handle type="target" position={Position.Top} className="!bg-red-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
          <Shield className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">Audit Gate</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-red-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
ComplianceNode.displayName = "ComplianceNode";

/* ─── Payer Action Node ─── */

export const PayerActionNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Payer Action";

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-blue-500">
      <Handle type="target" position={Position.Top} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-background" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
          <CreditCard className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground">Payer Integration</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
PayerActionNode.displayName = "PayerActionNode";

/* ─── Trigger Node (Workflow entry point) ─── */

const WORKFLOW_TRIGGER_STYLES: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  webhook: { icon: <Zap className="h-4 w-4" />, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" },
  schedule: { icon: <Calendar className="h-4 w-4" />, color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-900/30" },
  clinical_event: { icon: <Stethoscope className="h-4 w-4" />, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/30" },
  claims_event: { icon: <CreditCard className="h-4 w-4" />, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" },
  referral_event: { icon: <FileText className="h-4 w-4" />, color: "text-cyan-600 dark:text-cyan-400", bg: "bg-cyan-100 dark:bg-cyan-900/30" },
  manual: { icon: <Hand className="h-4 w-4" />, color: "text-zinc-600 dark:text-zinc-400", bg: "bg-zinc-100 dark:bg-zinc-800" },
};

export const WorkflowTriggerNode = memo(({ data, selected }: NodeProps) => {
  const name = (data.name as string) ?? "Trigger";
  const triggerType = (data.triggerType as string) ?? "webhook";
  const style = WORKFLOW_TRIGGER_STYLES[triggerType] ?? WORKFLOW_TRIGGER_STYLES.webhook;

  return (
    <WorkflowNodeWrapper selected={selected} data={data as Record<string, unknown>} selectedBorderColor="border-blue-500">
      <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.bg, style.color)}>
          {style.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground capitalize">{triggerType.replace(/_/g, " ")}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3 !border-2 !border-background" />
    </WorkflowNodeWrapper>
  );
});
WorkflowTriggerNode.displayName = "WorkflowTriggerNode";

/* ─── Start / End (reuse from Agent Studio pattern) ─── */

export const WfStartNode = memo(({ data, selected }: NodeProps) => (
  <div
    className={cn(
      "relative flex h-12 w-12 items-center justify-center rounded-full border-2 bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 transition-all",
      selected ? "border-emerald-500 shadow-md" : "border-emerald-300 dark:border-emerald-700",
      execClasses((data ?? {}) as Record<string, unknown>),
    )}
  >
    <ExecBadge state={(data as Record<string, unknown>)?.__execState as string | undefined} />
    <CircleDot className="h-5 w-5" />
    <Handle type="source" position={Position.Bottom} className="!bg-emerald-500 !w-3 !h-3 !border-2 !border-background" />
  </div>
));
WfStartNode.displayName = "WfStartNode";

export const WfEndNode = memo(({ data, selected }: NodeProps) => (
  <div
    className={cn(
      "relative flex h-12 w-12 items-center justify-center rounded-full border-2 bg-zinc-50 text-zinc-500 dark:bg-zinc-900 dark:text-zinc-400 transition-all",
      selected ? "border-zinc-500 shadow-md" : "border-zinc-300 dark:border-zinc-700",
      execClasses((data ?? {}) as Record<string, unknown>),
    )}
  >
    <ExecBadge state={(data as Record<string, unknown>)?.__execState as string | undefined} />
    <Square className="h-5 w-5" />
    <Handle type="target" position={Position.Top} className="!bg-zinc-500 !w-3 !h-3 !border-2 !border-background" />
  </div>
));
WfEndNode.displayName = "WfEndNode";
